import React, { useState, useEffect, useCallback } from "react";
import { NavLink, Outlet, useNavigate } from "react-router-dom";
import { Dices, Users, ServerCog, ShieldCheck, LogOut, Radar, MapPinned, FileChartColumn, Plane, WifiOff, Headset, NotebookPen, Gauge } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";
import { fmtHace } from "@/lib/format";
import { TooltipProvider } from "@/components/ui/tooltip";

const NAV = [
  { to: "/comando/matriz", label: "MATRIZ", hint: "Juegos y sorteos", icon: Dices, testid: "nav-matriz" },
  { to: "/comando/usuarios", label: "USUARIOS", hint: "Cuentas y planes", icon: Users, testid: "nav-usuarios" },
  { to: "/comando/mapa", label: "MAPA", hint: "Usuarios por zona", icon: MapPinned, testid: "nav-mapa" },
  { to: "/comando/informes", label: "INFORMES", hint: "Reportes ejecutivos", icon: FileChartColumn, testid: "nav-informes" },
  { to: "/comando/soporte", label: "SOPORTE", hint: "Correos y tickets", icon: Headset, testid: "nav-soporte" },
  { to: "/comando/bitacora", label: "BITÁCORA", hint: "Incidencias", icon: NotebookPen, testid: "nav-bitacora" },
  { to: "/comando/medidor", label: "MEDIDOR", hint: "Velocidad en vivo", icon: Gauge, testid: "nav-medidor" },
  { to: "/comando/sala-de-maquinas", label: "SALA DE MÁQUINAS", hint: "Servicios y costes", icon: ServerCog, testid: "nav-sala" },
  { to: "/comando/auditoria", label: "AUDITORÍA", hint: "Accesos y acciones", icon: ShieldCheck, testid: "nav-auditoria" },
];

const ComandoLayout = () => {
  const navigate = useNavigate();
  const { logout } = useAuth();
  const [modoViaje, setModoViaje] = useState(false);
  const [stale, setStale] = useState(null);

  const cargarModo = useCallback(async () => {
    try {
      const { data } = await api.get("/comando/modo-viaje");
      if (typeof data?.activo === "boolean") setModoViaje(data.activo);
    } catch (e) { /* noop */ }
  }, []);

  useEffect(() => {
    cargarModo();
    const t = setInterval(cargarModo, 60000);
    const h = () => cargarModo();
    const onStale = (e) => setStale(e.detail?.at || true);
    const onFresh = () => setStale(null);
    window.addEventListener("axiora-modoviaje", h);
    window.addEventListener("axiora-bridge-stale", onStale);
    window.addEventListener("axiora-bridge-fresh", onFresh);
    return () => {
      clearInterval(t);
      window.removeEventListener("axiora-modoviaje", h);
      window.removeEventListener("axiora-bridge-stale", onStale);
      window.removeEventListener("axiora-bridge-fresh", onFresh);
    };
  }, [cargarModo]);

  const salir = () => { logout(); navigate("/acceso-comando", { replace: true }); };

  return (
    <TooltipProvider delayDuration={150}>
      <div className="min-h-screen bg-[#050505] flex">
        {/* Riel angosto de iconos */}
        <aside data-testid="sidebar" className="w-[76px] md:w-52 shrink-0 h-screen sticky top-0 bg-[#0A0A0A] border-r border-[#1F1F24] flex flex-col">
          <div className="h-14 flex items-center justify-center md:justify-start md:px-4 gap-2 border-b border-[#1F1F24] w-full">
            <Radar className="w-5 h-5 text-[#D4AF37] shrink-0" />
            <span className="hidden md:inline font-serif-ax text-[#D4AF37] tracking-[0.2em] text-sm">AXIORA</span>
          </div>

          <nav className="flex-1 py-3 px-1.5 md:px-2 flex flex-col gap-1 overflow-y-auto">
            {NAV.map(({ to, label, hint, icon: Icon, testid }) => (
              <NavLink
                key={to}
                to={to}
                data-testid={testid}
                aria-label={label}
                title={hint}
                className={({ isActive }) =>
                  `flex flex-col md:flex-row items-center md:items-center gap-1 md:gap-3 px-1 md:px-3 py-2 rounded-lg transition-colors border ${
                    isActive
                      ? "bg-[#D4AF37]/12 text-[#FFD700] border-[#D4AF37]/30"
                      : "text-[#A1A1AA] hover:text-[#F5F5F7] hover:bg-white/[0.04] border-transparent"
                  }`
                }
              >
                <Icon className="w-5 h-5 shrink-0" />
                <span className="min-w-0 text-center md:text-left">
                  <span className="block text-[8px] md:text-[11px] font-semibold tracking-wider leading-tight">{label}</span>
                  <span className="hidden md:block text-[10px] text-[#71717A] leading-tight truncate">{hint}</span>
                </span>
              </NavLink>
            ))}
          </nav>

          <div className="p-1.5 md:p-2 w-full border-t border-[#1F1F24]">
            <button
              data-testid="logout-btn"
              onClick={salir}
              aria-label="Cerrar sesión"
              className="w-full flex flex-col md:flex-row items-center gap-1 md:gap-3 px-1 md:px-3 py-2 rounded-lg text-[#A1A1AA] hover:text-[#FF6B5B] hover:bg-[#FF6B5B]/5 transition-colors"
            >
              <LogOut className="w-5 h-5 shrink-0" />
              <span className="text-[8px] md:text-[11px] font-semibold tracking-wider">CERRAR SESIÓN</span>
            </button>
          </div>
        </aside>

        {/* Contenido */}
        <main className="flex-1 min-w-0 h-screen overflow-y-auto">
          {stale && (
            <div data-testid="banner-reconectando" className="sticky top-0 z-30 flex items-center gap-2 px-6 py-2.5 bg-[#F39C12]/15 border-b border-[#F39C12]/40 text-[#F39C12] text-sm font-medium">
              <WifiOff className="w-4 h-4" /> Reconectando con el puente — mostrando últimos datos recibidos
              {typeof stale === "string" && <span className="font-mono-ax text-[11px] opacity-80">· {fmtHace(stale)}</span>}
            </div>
          )}
          {modoViaje && (
            <div data-testid="banner-modo-viaje" className="sticky top-0 z-20 flex items-center gap-2 px-6 py-2.5 bg-[#1e3a5f] border-b border-[#2f6fb0] text-[#cfe6ff] text-sm font-medium">
              <Plane className="w-4 h-4" /> MODO VIAJE ACTIVO — El Guardián opera en modo autónomo
            </div>
          )}
          <Outlet />
        </main>
      </div>
    </TooltipProvider>
  );
};

export default ComandoLayout;
